import React, { useEffect, useRef } from "react";
import { Switch, Route, Redirect, useHistory } from "react-router-dom";
import { MsalProvider, useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { msalInstance } from "../../core/authentication/entra/msal";
import i18n from "../../i18n";
import Header from "../shared-ui/header";
import routes from "../../routes/routes";

// handles the /admin/login/callback hash -> tokens -> redirect
import AdminRedirectHandler from "./AdminRedirectHandler";

const ADMIN_LANDING = "/admin/testlist";

/** Guard for admin routes: if not signed in, bounce to /admin to trigger login */
const AdminGuardRoute: React.FC<{ path: string; component: any; exact?: boolean }> = ({
  path,
  component: Component,
  exact,
}) => {
  const { accounts, inProgress } = useMsal();

  if (inProgress !== InteractionStatus.None) {
    return <div>Loading...</div>;
  }
  if (accounts.length === 0) {
    return <Redirect to="/admin" />;
  }
  return <Route exact={exact} path={path} render={(props) => <Component {...props} />} />;
};

/** First page that triggers login if needed, otherwise goes to ADMIN_LANDING */
const AdminEntry: React.FC = () => {
  const history = useHistory();
  const { instance, accounts, inProgress } = useMsal();
  const started = useRef(false);

  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return;

    if (accounts.length > 0) {
      // already signed in -> go to first admin screen
      history.replace(ADMIN_LANDING);
      return;
    }
    if (!started.current) {
      started.current = true;
      sessionStorage.setItem("msal.redirectStartPage", ADMIN_LANDING);
      instance.loginRedirect({ scopes: ["openid", "profile", "email", process.env.REACT_APP_ADMIN_API_SCOPE!] });
    }
  }, [inProgress, accounts.length, instance, history]);

  return <div>Starting admin sign-in...</div>;
};

const isAdminRoute = (r: any) => {
  const p = r?.path;
  if (typeof p === "string") return p.startsWith("/admin");
  if (Array.isArray(p)) return p.some((s) => typeof s === "string" && s.startsWith("/admin"));
  return false;
};

const AdminApp: React.FC = () => {
  const adminRoutes = routes.filter(isAdminRoute);

  return (
    <MsalProvider instance={msalInstance}>
      <>
      <Header showHeader={true} location={{ pathname: "/admin"}} 
      t={i18n.t.bind(i18n)}
      />

      <Switch>
        <Route exact path="/admin/login/callback" component={AdminRedirectHandler} />
        <Route exact path="/admin" component={AdminEntry} />

        {adminRoutes.map((r: any, i: number) => (
          <AdminGuardRoute
            key={String(r.path ?? i)}
            path={r.path}
            exact={r.exact}
            component={r.component}
          />
        ))}

        <Redirect to={ADMIN_LANDING} />
      </Switch>
      </>
    </MsalProvider>
  );
};

export default AdminApp;
